import React, { useEffect, useState } from 'react';
import { getArticles } from '../services/contentService';
import { ArticleWithAuthor } from '../types';
import { ArticleCard } from '../components/ArticleCard';
import { SEOHead } from '../components/SEOHead';
import { Newspaper, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

export const NewsHub: React.FC = () => {
    const [articles, setArticles] = useState<ArticleWithAuthor[]>([]);
    const [visibleCount, setVisibleCount] = useState(10);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getArticles(undefined, undefined, 50).then(data => {
            setArticles(data);
            setLoading(false);
        });
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center py-24">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-400"></div>
            </div>
        );
    }

    const featured = articles[0];
    const remaining = articles.slice(1, visibleCount);

    return (
        <>
            <SEOHead
                title="News Hub"
                description="Stay informed with the latest investigative reporting, deep dives, and analysis from Nida Studios."
            />
            <div className="animate-fade-in pb-12">
                <header className="mb-12 border-b border-brand-900 pb-4 flex items-center gap-3">
                    <div className="p-2 bg-brand-100 rounded-lg text-brand-400">
                        <Newspaper size={24} />
                    </div>
                    <h1 className="text-2xl font-black font-sans text-brand-900 uppercase tracking-tight">The News Hub</h1>
                </header>

                {featured && (
                    <section className="mb-16">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            <div className="lg:col-span-8 group relative overflow-hidden rounded-3xl aspect-video shadow-2xl">
                                <Link to={`/news/${featured.category.toLowerCase()}/${featured.slug || featured.id}`} className="block h-full w-full">
                                    <div className="absolute inset-0 bg-brand-900/20 group-hover:bg-brand-900/0 transition-colors z-10" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-brand-950 via-brand-950/40 to-transparent opacity-90 z-20" />

                                    <img
                                        src={featured.coverImage}
                                        alt={featured.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105"
                                    />

                                    <div className="absolute bottom-0 left-0 w-full p-8 md:p-12 z-30 flex flex-col justify-end h-full">
                                        <div className="transform transition-transform duration-500 group-hover:-translate-y-2">
                                            <div className="flex items-center gap-3 mb-4">
                                                <span className="bg-brand-400 text-white text-[10px] font-bold px-3 py-1 uppercase tracking-widest rounded-full">{featured.category}</span>
                                                <span className="text-white/80 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2">
                                                    <Clock size={12} /> {featured.readTime} min read
                                                </span>
                                            </div>

                                            <h2 className="text-2xl md:text-4xl font-serif font-black text-white leading-tight mb-4">
                                                {featured.title}
                                            </h2>

                                            <p className="hidden md:block text-base text-gray-200 font-serif leading-relaxed line-clamp-2 max-w-2xl">
                                                {featured.excerpt}
                                            </p>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </section>
                )}

                <section className="mb-16">
                    <h2 className="text-lg font-bold text-brand-900 uppercase tracking-widest mb-8 border-b border-gray-100 pb-2">Latest Updates</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        <div className="lg:col-span-8 space-y-12">
                            {remaining.map(article => (
                                <ArticleCard key={article.id} article={article} />
                            ))}

                            {articles.length > visibleCount && (
                                <div className="text-center pt-16">
                                    <button
                                        onClick={() => setVisibleCount(v => v + 10)}
                                        className="px-12 py-4 bg-white border-2 border-brand-900 font-black uppercase tracking-[0.2em] text-[10px] hover:bg-brand-400 hover:border-brand-400 hover:text-white transition-all transform hover:scale-105"
                                    >
                                        Load More Stories
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};
